<template>
  <div class="search-wrapper">
    <form class="form-inline search" @submit.prevent="onSubmit">
      <div class="input-group" :class="{ 'is-focused': isFocused }">
        <div class="input-group-prepend">
          <span class="input-group-text text-muted">
            <i :class="isAiLoading ? 'fa fa-spinner fa-spin fa-fw' : 'fa fa-search fa-fw'"></i>
          </span>
        </div>
        <input
          type="text"
          class="form-control search-field"
          :placeholder="aiMode ? 'Ask in plain English...' : 'Search'"
          v-model="searchText"
          @focus="isFocused = true"
          @blur="isFocused = false"
        >
        <div class="input-group-btn">
          <button
            type="button"
            class="btn btn-link ai-toggle"
            :class="{ 'ai-toggle--active': aiMode }"
            @click.prevent="aiMode = !aiMode"
            title="Toggle AI search"
          ><img src="~@app/images/ai-search.png" alt="AI Search" class="ai-toggle-icon" /></button>
          <button type="button" class="btn btn-link text-muted" @click.prevent="$refs.searchHelp.show">
            <i class="fa fa-question"></i>
          </button>
          <button type="button" class="btn btn-link text-muted" @click.prevent="resetSearch" v-if="searchText || hasAiFilters">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>

      <search-help ref="searchHelp"></search-help>
    </form>

    <div v-if="hasAiFilters" class="filter-chips">
      <span v-for="(value, key) in aiFilters" :key="key" class="chip">
        {{ key }}: {{ value }}
        <button type="button" @click="removeFilter(key)">×</button>
      </span>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";

import SearchHelp from "@app/components/admin/api_requests/SearchHelp";

const AI_FILTER_KEYS = ['provider', 'status', 'api_name', 'from_date', 'to_date'];

export default {
  props: {
    q: String
  },

  components: {
    SearchHelp
  },

  data() {
    return {
      isFocused: false,
      searchText: null,
      aiMode: false,
      isAiLoading: false,
      aiFilters: {}
    };
  },

  computed: {
    hasAiFilters() {
      return Object.keys(this.aiFilters).length > 0;
    }
  },

  methods: {
    async onSubmit() {
      const q = this.searchText && this.searchText.trim();
      if (!q) return;

      if (this.aiMode) {
        this.isAiLoading = true;
        try {
          const { data } = await axios.post('/api/ai/search', { query: q });
          this.aiFilters = data;
          this.$router.push({
            name: 'apiRequestsList',
            query: { ...data, sort: 'created_at', direction: 'desc' }
          });
        } catch (e) {
          this.$router.push({ name: 'apiRequestsList', query: { q } });
        } finally {
          this.isAiLoading = false;
        }
      } else {
        this.aiFilters = {};
        this.$router.push({ name: 'apiRequestsList', query: { q } });
      }
    },

    removeFilter(key) {
      const updated = { ...this.aiFilters };
      delete updated[key];
      this.aiFilters = updated;
      const query = { ...this.$route.query };
      delete query[key];
      this.$router.push({ name: 'apiRequestsList', query });
    },

    resetSearch() {
      this.searchText = null;
      this.aiFilters = {};
      this.$router.push({ name: 'apiRequestsList' });
    }
  },

  mounted() {
    this.searchText = this.q;
    const existing = {};
    AI_FILTER_KEYS.forEach(k => {
      if (this.$route.query[k]) existing[k] = this.$route.query[k];
    });
    if (Object.keys(existing).length) {
      this.aiFilters = existing;
      this.aiMode = true;
    }
  }
};
</script>

<style scoped>
.search-wrapper {
  margin-left: 1rem;
  width: 100%;
}

@media (max-width: 575.98px) {
  .search-wrapper {
    margin-left: 0;
    margin-top: 0.5rem;
  }
}

@media (min-width: 576px) {
  .search-wrapper {
    width: 65%;
  }
}

form.search {
  width: 100%;
}

.search input {
  background: transparent;
  border: none;
  outline: none;
  box-shadow: none;
}

.search .input-group {
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0);
  transition: background 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  width: 100%;
}

.search .input-group.is-focused {
  background: rgba(255, 255, 255, 1);
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.search .input-group-text {
  background: transparent;
  border: none;
  padding-right: 0;
}

.ai-toggle {
  padding: 0 0.3rem;
  opacity: 0.55;
  transition: opacity 0.2s;
}

.ai-toggle:hover {
  opacity: 0.85;
}

.ai-toggle--active {
  opacity: 1;
}

.ai-toggle-icon {
  width: 40px;
  height: 30px;
  display: block;
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.4rem;
}

.chip {
  display: inline-flex;
  align-items: center;
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 1rem;
  padding: 0.15rem 0.6rem;
  font-size: 0.8rem;
  color: #1565c0;
}

.chip button {
  background: none;
  border: none;
  cursor: pointer;
  margin-left: 0.3rem;
  padding: 0;
  color: #1565c0;
  font-size: 1rem;
  line-height: 1;
}
</style>
